/**
 * RenewalDefaultSetting: /video-renewal 전용 리뉴얼 회의 진입 페이지.
 *
 * v1 원본: pages/RenewalDefaultSetting/DefaultSettingPresenter.js
 *          + RenewalComponents/DefaultDeviceSetting/DefaultDeviceSettingPresenter.js
 *
 * CSS 구조:
 *   .rtcNew_container.rtcNew_container--setting.notosans_font
 *     └─ .rtcNew_enterWrap
 *         ├─ .rtcNew_enterWrap__inBox
 *         │   ├─ .rtcNew_enterWrap__leftBox  (CameraPreview + device buttons)
 *         │   └─ .rtcNew_enterWrap__rightBox (탭 + 설정)
 *         └─ .rtcNew_enterWrap__footer (참여하기 버튼)
 */
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import Lottie from 'lottie-web';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { localActions } from '@/store/slices/localSlice';
import { uiActions } from '@/store/slices/uiSlice';
import { conferenceActions } from '@/store/slices/conferenceSlice';
import { getUserIdentify } from '@/utils/userIdentificationCode';
import { isMasterUser, masterActions } from '@/store/slices/masterSlice';
import { isSameUserId } from '@/utils/participantUtils';
import type { MasterUser } from '@/types/store';
import { virtualBackgroundActions } from '@/store/slices/virtualBackgroundSlice';
import { captionActions } from '@/store/slices/captionSlice';
import { voiceNoteActions } from '@/store/slices/voiceNoteSlice';
import { translationActions } from '@/store/slices/translationSlice';
import { useTimeAgo } from '@/hooks/useTimeAgo';
import { createVirtualBackgroundEffect } from '@/utils/createVirtualBackgroundEffect';
import type { JitsiStreamBackgroundEffect } from '@/utils/JitsiStreamBackgroundEffect';
import { useVirtualBackgroundImages } from '@/hooks/useVirtualBackgroundImages';
import apiManager from '@/api';
import apiConfig from '@/api/config';
import DefaultHeader from '@/components/Header/DefaultHeader';
import { toastActions } from '@/store/slices/toastSlice';
import LUXDialog from '@/components/Dialog';
import ParticipantListDialog from '@/components/ParticipantListDialog/ParticipantListDialog';
import type { ParticipantItem } from '@/types/components';
import RoomPassword from '@/components/RoomPassword/RoomPassword';
import { toAbsoluteProfileUrl } from '@/utils/profileUtils';
import { isHttpNotFoundError, isRoomExplicitlyEnded } from '@/utils/conferenceUtils';
import { isRecorderMode } from '@/utils/recorderMode';

const MAX_ACCESS_USER = 50;
const MAX_USER_BG_MEMBER = 10;
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_EXTENSIONS = ['png', 'jpg', 'jpeg', 'svg'];

// 가상배경 이미지
const bgImages = [
  { id: 1, name: '은은한',     circle: new URL('@/assets/img/virtual-bg/img_chat_01.png', import.meta.url).href, full: '/images/vbg/background-1.jpg' },
  { id: 2, name: '스터디카페', circle: new URL('@/assets/img/virtual-bg/img_chat_02.png', import.meta.url).href, full: '/images/vbg/background-2.jpg' },
  { id: 3, name: '사무실',     circle: new URL('@/assets/img/virtual-bg/img_chat_03.png', import.meta.url).href, full: '/images/vbg/background-3.jpg' },
  { id: 4, name: '석양',       circle: new URL('@/assets/img/virtual-bg/img_chat_04.png', import.meta.url).href, full: '/images/vbg/background-4.jpg' },
  { id: 5, name: '해변',       circle: new URL('@/assets/img/virtual-bg/img_chat_05.png', import.meta.url).href, full: '/images/vbg/background-5.jpg' },
  { id: 6, name: '시티뷰',     circle: new URL('@/assets/img/virtual-bg/img_chat_06.png', import.meta.url).href, full: '/images/vbg/background-6.jpg' },
  { id: 7, name: '기하학적인', circle: new URL('@/assets/img/virtual-bg/img_chat_07.png', import.meta.url).href, full: '/images/vbg/background-7.jpg' },
  { id: 8, name: '오로라',     circle: new URL('@/assets/img/virtual-bg/img_chat_08.png', import.meta.url).href, full: '/images/vbg/background-8.jpg' },
  { id: 9, name: '현대적인',   circle: new URL('@/assets/img/virtual-bg/img_chat_09.png', import.meta.url).href, full: '/images/vbg/background-9.jpg' },
];

const AVATAR_LIST = [
  { value: 'jangok', tag: 'avartar_man', label: '아바타 1', img: 'https://static.wehago.com/imgs/rtc/avartar_man.png' },
  { value: 'suzy', tag: 'avartar_boy', label: '아바타 2', img: 'https://static.wehago.com/imgs/rtc/avartar_boy.png' },
  { value: 'jessie', tag: 'avartar_woman', label: '아바타 3', img: 'https://static.wehago.com/imgs/rtc/avartar_woman.png' },
];

const getAvatarTag = (value: string) => {
  return AVATAR_LIST.find((a) => a.value === value)?.tag || 'avartar_man';
};



// ── 컴포넌트 ─────────────────────────────────────────────────────────────────

interface Props {
  onJoinRoom: () => void;
}

type Tab = 'CONFERENCE' | 'VIRTUAL-BACKGROUND';

const RenewalDefaultSetting: React.FC<Props> = ({ onJoinRoom }) => {
  const dispatch = useAppDispatch();
  const local = useAppSelector((s) => s.local);
  const roomStatus = useAppSelector((s) => s.ui.roomStatus);
  const masterUsers = useAppSelector((s) => s.master.masterUsers);

  // refs
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const animFrameRef = useRef<number>(0);
  const joinedRef = useRef(false);
  // 대기방(t_room_ready_user)에 행을 등록했는지 여부. 등록하지 않았으면 삭제도 보내지 않는다.
  const readyRegisteredRef = useRef(false);
  const effectRef = useRef<JitsiStreamBackgroundEffect | null>(null);
  const processedStreamRef = useRef<MediaStream | null>(null);
  const selectedBgRef = useRef('none');
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const deviceInitPromiseRef = useRef<Promise<void> | null>(null);

  // state
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedCameraId, setSelectedCameraId] = useState('');
  const [selectedMicId, setSelectedMicId] = useState('');
  const [selectedSpeakerId, setSelectedSpeakerId] = useState('');
  const [userName, setUserName] = useState('');
  const [isJoining, setIsJoining] = useState(false);
  const [selectedBg, setSelectedBg] = useState('none');
  const [isSpeechEnabled, setIsSpeechEnabled] = useState(true);
  const [tab, setTab] = useState<Tab>('CONFERENCE');
  const [isAudioDropOpen, setIsAudioDropOpen] = useState(false);
  const [isCameraDropOpen, setIsCameraDropOpen] = useState(false);
  const [isMicOn, setIsMicOn] = useState(!local.isMuteOnJoin);
  const [isCamOn, setIsCamOn] = useState(true);
  const [hoverBgImageId, setHoverBgImageId] = useState<string | null>(null);
  const { userImages: userBgImages, userImagesLoaded, isGuest: isGuestVbg, loadUserImages, uploadImage, deleteImage } = useVirtualBackgroundImages();
  const showSnackbar = useCallback((type: 'success' | 'error' | 'info', message: string) => {
    dispatch(toastActions.showSnackbar({ type, message }));
  }, [dispatch]);

  // 회의 진입 로딩 Lottie
  const loaderLottieRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!isJoining || !loaderLottieRef.current) return;
    const anim = Lottie.loadAnimation({
      container: loaderLottieRef.current,
      renderer: 'svg',
      loop: true,
      autoplay: true,
      path: 'https://static.wehago.com/imgs/rtc/lottie/loading.json',
    });
    return () => { anim.destroy(); };
  }, [isJoining]);
  const [showPermissionDialog, setShowPermissionDialog] = useState(false);
  const [selectLocale, setSelectLocale] = useState<'ko' | 'en' | 'ja' | 'ru'>('ko');
  const [isLocaleDropOpen, setIsLocaleDropOpen] = useState(false);
  const [isParticipantDialogOpen, setIsParticipantDialogOpen] = useState(false);
  const [participantList, setParticipantList] = useState<ParticipantItem[]>([]);
  /** 참석자 목록 fetch 완료 여부 — 로딩 중 빈 배열을 '명단 없음'으로 오판하지 않기 위함 */
  const [participantsLoaded, setParticipantsLoaded] = useState(false);

  const localeOptions = [
    { value: 'ko' as const, label: '한국어' },
    { value: 'en' as const, label: 'English' },
    { value: 'ja' as const, label: '日本語' },
    { value: 'ru' as const, label: 'Русский' },
  ];

  const cameras = devices.filter((d) => d.kind === 'videoinput');
  const mics = devices.filter((d) => d.kind === 'audioinput');
  const speakers = devices.filter((d) => d.kind === 'audiooutput');

  // ── 디바이스 열거 ──
  const enumerateDevices = useCallback(async () => {
    const all = await navigator.mediaDevices.enumerateDevices();
    setDevices(all);
    dispatch(localActions.setDevices({
      audioInputDevices: all.filter((d) => d.kind === 'audioinput'),
      videoInputDevices: all.filter((d) => d.kind === 'videoinput'),
      audioOutputDevices: all.filter((d) => d.kind === 'audiooutput'),
    }));
    setSelectedCameraId((prev) => prev || all.find((d) => d.kind === 'videoinput')?.deviceId || '');
    setSelectedMicId((prev) => prev || all.find((d) => d.kind === 'audioinput')?.deviceId || '');
    setSelectedSpeakerId((prev) => prev || all.find((d) => d.kind === 'audiooutput')?.deviceId || '');
  }, [dispatch]);

  // ── 가상배경 효과 ──
  const stopCurrentEffect = useCallback(() => {
    if (effectRef.current) { effectRef.current.stopEffect(); effectRef.current = null; }
    processedStreamRef.current = null;
  }, []);

  const applyBgEffect = useCallback((bgId: string, rawStream: MediaStream) => {
    stopCurrentEffect();
    if (bgId === 'none') { if (videoRef.current) videoRef.current.srcObject = rawStream; return; }

    let backgroundType: 'blur' | 'image';
    let blurValue: number | undefined;
    let virtualSource: string | undefined;
    if (bgId === 'blur') { backgroundType = 'blur'; blurValue = 25; }
    else {
      const numId = Number(bgId);
      const bg = !isNaN(numId) ? bgImages.find((b) => b.id === numId) : undefined;
      if (bg) { backgroundType = 'image'; virtualSource = bg.full; }
      else {
        // 사용자 업로드 이미지
        const userBg = userBgImages.find((b) => b.id === bgId);
        if (userBg) { backgroundType = 'image'; virtualSource = userBg.src; }
        else return;
      }
    }

    createVirtualBackgroundEffect({ backgroundType, blurValue, virtualSource })
      .then((effect) => {
        if (!effect || !streamRef.current || !videoRef.current) return;
        effectRef.current = effect;
        const processed = effect.startEffect(streamRef.current);
        processedStreamRef.current = processed;
        videoRef.current.srcObject = processed;
      })
      .catch(() => { if (videoRef.current && streamRef.current) videoRef.current.srcObject = streamRef.current; });
  }, [stopCurrentEffect, userBgImages]);

  // ── 카메라 미리보기 ──
  const startPreview = useCallback(async (deviceId: string) => {
    stopCurrentEffect();
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: deviceId ? { deviceId: { exact: deviceId } } : true,
        audio: false,
      });
      streamRef.current = stream;
      if (selectedBgRef.current !== 'none') applyBgEffect(selectedBgRef.current, stream);
      else if (videoRef.current) videoRef.current.srcObject = stream;
    } catch { /* 카메라 사용 불가 */ }
  }, [stopCurrentEffect, applyBgEffect]);

  // ── 마이크 입력 인디케이터 ──
  const stopAudioIndicator = useCallback(() => {
    cancelAnimationFrame(animFrameRef.current);
    if (audioCtxRef.current) { audioCtxRef.current.close().catch(() => {}); audioCtxRef.current = null; }
  }, []);

  // ── 참석자 목록 fetch ──
  useEffect(() => {
    if (!local.roomId) return;
    let cancelled = false;
    const fetchParticipants = async () => {
      try {
        const roomInfo = await apiManager.getVideoRoomInfo(local.roomId);
        const accessUsers: Array<Record<string, unknown>> = (roomInfo as Record<string, unknown>).access_user as Array<Record<string, unknown>> || [];
        const mapped: ParticipantItem[] = accessUsers.map((u) => ({
          user: String(u.value || ''),
          user_name: u.type !== 'portal_id' ? String(u.value || '') : String(u.name || ''),
          user_type: u.type !== 'portal_id' ? 'email' as const : 1,
          profile_url: toAbsoluteProfileUrl(String(u.profile_url || '')) || '',
          is_joined: false,
          is_master: !!u.is_master,
        }));

        try {
          const connecting = local.isGuest
            ? await apiManager.getConnectingUserUnAuth(local.roomId)
            : await apiManager.getConnectingUserAuth(local.roomId);
          const connMap = new Map(connecting.map((c: Record<string, unknown>) => [String(c.user || c.user_id || ''), c]));
          for (const p of mapped) {
            const conn = connMap.get(p.user);
            if (conn) {
              p.is_joined = true;
              p.is_master = !!(conn as Record<string, unknown>).is_master;
              connMap.delete(p.user);
            }
          }
          for (const [, c] of connMap) {
            const cu = c as Record<string, unknown>;
            mapped.push({
              user: String(cu.user || cu.user_id || ''),
              user_name: String(cu.user_name || cu.name || ''),
              user_type: 1,
              profile_url: toAbsoluteProfileUrl(String(cu.profile_url || '')) || '',
              is_joined: true,
              is_master: !!cu.is_master,
            });
          }
        } catch { /* connecting user fetch 실패 무시 */ }

        // 위하고 회원(user_type === 1)은 실명/직급/소속을 별도 조회해 채운다.
        if (!local.isGuest) {
          try {
            const portalIds = Array.from(
              new Set(mapped.filter((p) => p.user_type === 1 && p.user).map((p) => p.user)),
            );
            const userInfos = await apiManager.getUserInfoList(portalIds);
            const infoMap = new Map(userInfos.map((info) => [String(info.portal_id), info]));
            for (const p of mapped) {
              const info = infoMap.get(p.user);
              if (!info) continue;
              if (info.user_name) p.user_name = info.user_name;
              if (info.rank_name) p.rank_name = info.rank_name;
              // 소속 경로: 'A > B > C' 형태 (InfoTab의 조직 경로 표기와 동일)
              const orgPath = info.full_path_list?.length
                ? info.full_path_list.map((o) => o.organization_name).filter(Boolean).join(' > ')
                : '';
              p.full_path = orgPath || info.full_path || undefined;
              if (info.profile_url) {
                p.profile_url = toAbsoluteProfileUrl(String(info.profile_url)) || p.profile_url;
              }
            }
          } catch { /* 직원정보 조회 실패 시 기본 정보 유지 */ }
        }

        mapped.sort((a, b) => {
          if (a.is_joined !== b.is_joined) return a.is_joined ? -1 : 1;
          if (a.is_master !== b.is_master) return a.is_master ? -1 : 1;
          return (a.user_name || '').localeCompare(b.user_name || '');
        });
        if (cancelled) return;
        setParticipantList(mapped);
        setParticipantsLoaded(true);
        return true;
      } catch {
        /* roomInfo fetch 실패 — participantsLoaded는 false로 유지해 입장 시 재조회 */
        return false;
      }
    };
    // 실패 시 재시도(1s, 2s). 마스터/참석자 판정 근거이므로 첫 실패로 포기하지 않는다.
    void (async () => {
      for (const delay of [0, 1000, 2000]) {
        if (cancelled) return;
        if (delay > 0) await new Promise((r) => setTimeout(r, delay));
        if (cancelled) return;
        if (await fetchParticipants()) return;
      }
    })();
    return () => { cancelled = true; };
  }, [local.roomId, local.isGuest]);

  // ── 마스터 목록 선조회 ──
  useEffect(() => {
    if (local.isGuest || !local.token) return;
    if (masterUsers.length > 0) return;
    const token = local.token;
    let cancelled = false;
    void (async () => {
      for (const delay of [0, 1000, 2000]) {
        if (cancelled) return;
        if (delay > 0) await new Promise((r) => setTimeout(r, delay));
        if (cancelled) return;
        try {
          const list = await apiManager.getMasterUsers(token);
          if (cancelled) return;
          if (Array.isArray(list) && list.length > 0) {
            dispatch(masterActions.setMasterUsers(list as MasterUser[]));
            return;
          }
        } catch { /* 다음 시도 */ }
      }
      /* 최종 실패 시 creatorInfo/참석자 명단 fallback + 입장 시점 재조회로 판정 */
    })();
    return () => { cancelled = true; };
  }, [local.token, local.isGuest]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 생명주기 ──
  useEffect(() => {
    // recorder(Jibri)는 실제 카메라/마이크가 없어 권한 프롬프트만 뜨고 그 화면이
    // 그대로 녹화된다(권한 필요 팝업이 찍히는 버그). recorder는 이 프리조인 권한
    // 체크 자체를 건너뛴다.
    if (isRecorderMode()) {
      enumerateDevices();
    } else {
      deviceInitPromiseRef.current = navigator.mediaDevices
        .getUserMedia({ video: true, audio: true })
        .then((s) => { s.getTracks().forEach((t) => t.stop()); })
        .catch(() => {
          LUXDialog.alert(['브라우저 설정(상단 주소 표시줄 왼쪽의 아이콘)에서','마이크 또는 카메라 권한을 허용해주세요.'], {
            type: 'warning',
            title: '마이크 및 카메라 권한이 필요합니다.',
          });
        })
        .finally(() => enumerateDevices());
    }

    if (!local.selectedAvatar) {
      dispatch(localActions.setSelectedAvatar('jangok'));
    }
    // 저장된 아바타가 유효한 값인지 검증
    else if (!AVATAR_LIST.some((a) => a.value === local.selectedAvatar)) {
      dispatch(localActions.setSelectedAvatar('jangok'));
    }

    /**
     * 대기방 등록/해제
     */
    const roomId = local.roomId;
    const token = local.token;
    const userId = local.userId;
    let readyCancelled = false;

    const removeReadyUser = (onUnload: boolean) => {
      if (!readyRegisteredRef.current || joinedRef.current) return;
      readyRegisteredRef.current = false;
      if (onUnload) apiManager.deleteReadyUserOnUnload(roomId, token, userId);
      else apiManager.deleteReadyUser(roomId, token, userId).catch(() => {});
    };

    // 탭/브라우저 종료, 절전, 앱 백그라운드 전환에서는 언마운트 정리가 실행되지 않는다.
    const handleUnload = () => removeReadyUser(true);

    if (roomId && token && userId && !local.isGuest) {
      window.addEventListener('beforeunload', handleUnload);
      window.addEventListener('pagehide', handleUnload);

      void (async () => {
        let isMaster = isSameUserId(local.creatorInfo?.portal_id, userId);
        if (!isMaster) {
          try {
            const list = await apiManager.getMasterUsers(token);
            isMaster = Array.isArray(list) && isMasterUser(list as MasterUser[], userId);
          } catch {
            /* 판정 실패 시 등록하지 않는다. 방이 안 닫히는 쪽이 피해가 크다 */
          }
        }
        if (!isMaster) return;

        await apiManager.setReadyUserStatus(roomId, token, '1').catch(() => {});
        readyRegisteredRef.current = true;
        // 등록 응답이 언마운트 이후에 도착한 경우 바로 되돌린다
        if (readyCancelled) removeReadyUser(false);
      })();
    }

    navigator.mediaDevices.addEventListener('devicechange', enumerateDevices);
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', enumerateDevices);
      window.removeEventListener('beforeunload', handleUnload);
      window.removeEventListener('pagehide', handleUnload);
      stopAudioIndicator();
      if (effectRef.current) { effectRef.current.stopEffect(); effectRef.current = null; }
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      readyCancelled = true;
      removeReadyUser(false);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedCameraId && isCamOn) {
      startPreview(selectedCameraId);
      dispatch(localActions.setVideoInputDeviceId(selectedCameraId));
    }
  }, [selectedCameraId, isCamOn]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (selectedMicId) dispatch(localActions.setAudioInputDeviceId(selectedMicId)); }, [selectedMicId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedSpeakerId) dispatch(localActions.setAudioOutputDeviceId(selectedSpeakerId));
  }, [selectedSpeakerId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    selectedBgRef.current = selectedBg;
    if (streamRef.current) applyBgEffect(selectedBg, streamRef.current);
  }, [selectedBg]); // eslint-disable-line react-hooks/exhaustive-deps

  // 서버에서 사용자 업로드 이미지 목록 로드
  useEffect(() => {
    if (!userImagesLoaded) loadUserImages();
  }, [userImagesLoaded, loadUserImages]);

  // ── 예약회의 대기 카운트다운 ──
  const startDatetime = local.startDatetime;
  const handleTimerFinish = useCallback(() => {
    // startDatetime이 아직 로드되지 않은 상태(0)에서 useTimeAgo가
    // 즉시 완료 처리하는 경우를 무시한다.
    if (!startDatetime) return;
    dispatch(uiActions.setRoomStatus('SETUP'));
    dispatch(conferenceActions.setStatus('setup'));
  }, [dispatch, startDatetime]);

  const { days: waitDays, hours: waitHours, minutes: waitMinutes, seconds: waitSeconds, isFinished: isWaitFinished } = useTimeAgo(local.startDatetime || 0, handleTimerFinish);

  const waitingTimeText = useMemo(() => {
    if (isWaitFinished) return null; // 이미 종료됨
    const totalMin = waitDays * 24 * 60 + waitHours * 60 + waitMinutes + (waitSeconds > 0 ? 1 : 0);
    if (totalMin <= 1) return null; // 1분 이하 → 별도 문구
    if (totalMin < 60) return `약 ${totalMin}분`;
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return m > 0 ? `약 ${h}시간 ${m}분` : `약 ${h}시간`;
  }, [waitDays, waitHours, waitMinutes, waitSeconds, isWaitFinished]);

  const startDateStr = useMemo(() => {
    if (!local.startDatetime) return '';
    const d = new Date(local.startDatetime);
    const y = d.getFullYear();
    const m = d.getMonth() + 1;
    const day = d.getDate();
    const h = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${y}.${m}.${day} ${h}:${min}`;
  }, [local.startDatetime]);

  // ── approveBox Lottie 애니메이션 ──
  const approveLottieRef = useRef<HTMLDivElement>(null);
  const waitingLottieRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (roomStatus !== 'WAITING_APPROVAL' || !approveLottieRef.current) return;
    const anim = Lottie.loadAnimation({
      container: approveLottieRef.current,
      renderer: 'svg',
      loop: true,
      autoplay: true,
      path: 'https://static.wehago.com/imgs/rtc/lottie/loading.json',
    });
    return () => { anim.destroy(); };
  }, [roomStatus]);

  useEffect(() => {
    if (roomStatus !== 'WAITING' || !waitingLottieRef.current) return;
    const anim = Lottie.loadAnimation({
      container: waitingLottieRef.current,
      renderer: 'svg',
      loop: true,
      autoplay: true,
      path: 'https://static.wehago.com/imgs/rtc/lottie/loading.json',
    });
    return () => { anim.destroy(); };
  }, [roomStatus]);

  // ── 마스터 승인 대기 lpevent 롱폴링 ──
  const lpeventMountedRef = useRef(true);

  const handleRefusalClose = useCallback(() => {
    if (local.userId) {
      window.location.href = apiConfig.portalBaseUrl ? `${apiConfig.portalBaseUrl}/#/main` : '/';
    } else {
      window.close();
      setTimeout(() => { if (!window.closed) window.location.href = '/'; }, 200);
    }
  }, [local.userId]);

  const doFullJoin = useCallback(() => {
    if (local.roomId && local.token) apiManager.setReadyEnterUser(local.roomId, local.token, '2').catch(() => {});
    stopAudioIndicator();
    stopCurrentEffect();
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    joinedRef.current = true;
    setIsJoining(true);
    onJoinRoom();
  }, [local.roomId, local.token, stopAudioIndicator, stopCurrentEffect, onJoinRoom]);

  useEffect(() => {
    if (roomStatus !== 'WAITING_APPROVAL') return;
    lpeventMountedRef.current = true;

    const lpeventHandler = (data: {
      extra_data: { type: string; access_key?: string; status?: string };
    }) => {
      if (!lpeventMountedRef.current) return;
      const { type, access_key: accessKey, status } = data.extra_data;

      if (type === 'join_response') {
        if (status === 'allow' && accessKey) {
          dispatch(localActions.mergeRoomInfo({ token: accessKey }));
          // dispatch device settings before joining
          if (!isMicOn) { dispatch(localActions.setAudioInputDeviceId('')); dispatch(localActions.setAudioMuted(true)); }
          else if (selectedMicId) { dispatch(localActions.setAudioInputDeviceId(selectedMicId)); dispatch(localActions.setAudioMuted(false)); }
          if (selectedSpeakerId) dispatch(localActions.setAudioOutputDeviceId(selectedSpeakerId));
          dispatch(captionActions.setEnabled(isSpeechEnabled));
          dispatch(voiceNoteActions.setEnabled(isSpeechEnabled));
          const localeMap: Record<string, string> = { ko: 'ko', en: 'en', ja: 'ja', ru: 'ru' };
          dispatch(captionActions.setLanguage(selectLocale));
          dispatch(voiceNoteActions.setLanguage(localeMap[selectLocale] || 'ko'));
          dispatch(translationActions.setLanguage(selectLocale as 'ko' | 'en' | 'ja'));
          if (isCamOn && selectedCameraId) { dispatch(localActions.setVideoInputDeviceId(selectedCameraId)); dispatch(localActions.setVideoMuted(false)); }
          else { dispatch(localActions.setVideoInputDeviceId('')); dispatch(localActions.setVideoMuted(true)); }
          if (selectedBg === 'none') dispatch(virtualBackgroundActions.setBackground({ type: 'none' }));
          else if (selectedBg === 'blur') dispatch(virtualBackgroundActions.setBackground({ type: 'blur', blurValue: 25 }));
          else {
            const numId = Number(selectedBg);
            const bg = !isNaN(numId) ? bgImages.find((b) => b.id === numId) : undefined;
            if (bg) dispatch(virtualBackgroundActions.setBackground({ type: 'image', imageUrl: bg.full }));
            else {
              const userBg = userBgImages.find((b) => b.id === selectedBg);
              if (userBg) dispatch(virtualBackgroundActions.setBackground({ type: 'image', imageUrl: userBg.src }));
            }
          }
          doFullJoin();
        } else {
          dispatch(uiActions.setRoomStatus('CONFERENCE_REFUSAL'));
          LUXDialog.alert(['마스터가 참석 요청을 거절했습니다.', '다른 회의를 개설하거나 마스터에게 문의하세요.'], {
            type: 'warning',
            title: '참석 요청 거절',
            confirmCallback: handleRefusalClose,
            onCloseCallback: handleRefusalClose,
          });
        }
        return;
      }
      startLpevent();
    };

    const startLpevent = () => {
      if (!lpeventMountedRef.current) return;
      apiManager.lpevent(
        {
          room: local.roomId,
          userIdentify: local.userIdentify || getUserIdentify(),
          profileUrl: local.profileUrl,
          timeout: 30,
          jwt: local.token || undefined,
        },
        lpeventHandler,
      );
    };
    startLpevent();

    return () => { lpeventMountedRef.current = false; };
  }, [roomStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── 참여하기 ──
  const handleJoin = async (opts?: { passwordVerified?: boolean }) => {
    if (isJoining || roomStatus === 'WAITING_APPROVAL') return;
    setIsJoining(true);
    try {
      // 프리조인 진입 시 이미 권한 체크 및 알림을 띄우므로, 진입 버튼 클릭 시에는 권한 체크를 생략합니다.

      // 입장 시점에 재조회한 방 정보 — 마스터/참석자 판정에도 재사용한다.
      let roomInfo: Record<string, unknown> | null = null;

      // 방 상태 유효성 검사 (종료, 인원 초과)
      if (local.roomId) {
        try {
          roomInfo = await apiManager.getVideoRoomInfo(local.roomId) as Record<string, unknown>;
        } catch (err) {
          if (isHttpNotFoundError(err)) {
            setIsJoining(false);
            stopAudioIndicator();
            stopCurrentEffect();
            streamRef.current?.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
            if (videoRef.current) videoRef.current.srcObject = null;
            dispatch(uiActions.setRoomStatus('NOT_FOUND'));
            return;
          }
          // 방 정보 조회 실패 시 일단 진행
        }

        // 종료된 회의 체크
        if (roomInfo && isRoomExplicitlyEnded(roomInfo)) {
          setIsJoining(false);
          LUXDialog.alert(['화상회의가 삭제되었거나', '마스터가 이 회의를 종료했습니다.'], {
            type: 'info',
            title: '종료된 회의입니다.',
          });
          return;
        }

        try {
          // 참석 인원 초과 체크
          const connecting = local.isGuest
            ? await apiManager.getConnectingUserUnAuth(local.roomId)
            : await apiManager.getConnectingUserAuth(local.roomId);
          if (Array.isArray(connecting) && connecting.length >= MAX_ACCESS_USER) {
            setIsJoining(false);
            const closePage = () => {
              if (local.userId) {
                window.location.href = apiConfig.portalBaseUrl ? `${apiConfig.portalBaseUrl}/#/main` : '/';
              } else {
                window.close();
                setTimeout(() => { if (!window.closed) window.location.href = '/'; }, 200);
              }
            };
            LUXDialog.alert(['회의에 참석 가능한 인원은 최대 50명입니다.', '마스터에게 문의해주세요.'], {
              type: 'warning',
              title: '참석 인원이 초과하였습니다.',
              confirmCallback: closePage,
              onCloseCallback: closePage,
            });
            return;
          }
        } catch {
          // 참석 인원 조회 실패 시 일단 진행
        }
      }


      if (local.isGuest) {
        const guestName = userName.trim() || `참석자-${String(Math.floor(Math.random() * 1000000)).padStart(6, '0')}`;
        const guestUserId = local.userId || local.userIdentify || getUserIdentify();
        dispatch(localActions.setUserInfo({ portal_id: guestUserId, user_name: guestName, nickname: guestName, isGuest: true }));
      }


      // ── 마스터 / 참석자 판정 ──
      const freshAccessUsers: Array<Record<string, unknown>> | null =
        Array.isArray(roomInfo?.access_user)
          ? roomInfo!.access_user as Array<Record<string, unknown>>
          : null;
      const freshCreatorId = String(
        (roomInfo?.creator_info as Record<string, unknown> | undefined)?.portal_id ?? '',
      );

      const isRoomCreator = !!local.userId && !local.isGuest && (
        isSameUserId(local.creatorInfo?.portal_id, local.userId)
        || isSameUserId(freshCreatorId, local.userId)
      );

      // 마스터 목록이 아직 비어 있으면(선조회 실패/지연) 입장 시점에 한 번 더 직접 조회한다.
      let effectiveMasterUsers = masterUsers;
      if (!isRoomCreator && !local.isGuest && local.token && masterUsers.length === 0) {
        try {
          const list = await apiManager.getMasterUsers(local.token);
          if (Array.isArray(list) && list.length > 0) {
            effectiveMasterUsers = list as MasterUser[];
            dispatch(masterActions.setMasterUsers(effectiveMasterUsers));
          }
        } catch { /* 실패 시 나머지 근거로 판정 */ }
      }

      const isMasterLike = isRoomCreator || (!!local.userId && !local.isGuest && (
        isMasterUser(effectiveMasterUsers, local.userId)
        || participantList.some((p) => isSameUserId(p.user, local.userId) && p.is_master)
        || !!freshAccessUsers?.some((u) => isSameUserId(u.value, local.userId) && !!u.is_master)
      ));

      // 참석예정자 여부: 재조회한 access_user가 있으면 그것을, 없으면 화면 목록을 근거로 한다.
      const isInAccessList = !local.isGuest && !!local.userId && (
        freshAccessUsers
          ? freshAccessUsers.some((u) => isSameUserId(u.value, local.userId))
          : participantList.some((p) => isSameUserId(p.user, local.userId))
      );
      // 두 근거가 모두 없으면(둘 다 조회 실패) 판정 불가 — 보안상 fail-closed로 처리한다.
      const accessListUnknown = !freshAccessUsers && !participantsLoaded;

      console.log('[Join] gate', {
        securityType: local.securityType,
        userId: local.userId,
        isRoomCreator,
        isMasterLike,
        isInAccessList,
        accessListUnknown,
        masterUsersCount: effectiveMasterUsers.length,
        participantsLoaded,
        hasFreshAccessUsers: !!freshAccessUsers,
      });

      // ── PASSWORD방: 참여예정자가 아닌 사용자는 비밀번호 입력 필요 (생성자/마스터 제외) ──
      if (!isRoomCreator && local.securityType === 'PASSWORD' && !isMasterLike && !opts?.passwordVerified) {
        if (!isInAccessList) {
          dispatch(uiActions.setRoomStatus('PASSWORD_INPUT'));
          setIsJoining(false);
          return;
        }
      }

      // ── 마스터 승인방: 참여예정자가 아닌 사용자는 승인 필요 (생성자/마스터 제외) ──
      if (!isRoomCreator && local.securityType === 'MASTER_APPROVAL') {
        const isMaster = isMasterLike;
        const isInParticipantList = isInAccessList;
        if (!isMaster && !isInParticipantList) {
          const finalUserName = (local.isGuest ? userName.trim() : '') || local.userName || '사용자';
          try {
            await apiManager.requestJoinRoom({
              room: local.roomId,
              userIdentify: local.userIdentify || getUserIdentify(),
              username: finalUserName,
              profileUrl: local.profileUrl,
              isGuest: local.isGuest,
            });
          } catch { /* 요청 실패 시에도 대기 상태로 전환 */ }
          dispatch(uiActions.setRoomStatus('WAITING_APPROVAL'));
          setIsJoining(false);
          return;
        }
      }

      if (!isMicOn) { dispatch(localActions.setAudioInputDeviceId('')); dispatch(localActions.setAudioMuted(true)); }
      else if (selectedMicId) { dispatch(localActions.setAudioInputDeviceId(selectedMicId)); dispatch(localActions.setAudioMuted(false)); }
      if (selectedSpeakerId) dispatch(localActions.setAudioOutputDeviceId(selectedSpeakerId));
      dispatch(captionActions.setEnabled(isSpeechEnabled));
      dispatch(voiceNoteActions.setEnabled(isSpeechEnabled));
      // 자막/번역 언어를 caption + voiceNote 슬라이스에 반영
      const localeMap: Record<string, string> = { ko: 'ko-KR', en: 'en-US', ja: 'ja-JP', ru: 'ru-RU' };
      dispatch(captionActions.setLanguage(selectLocale));
      dispatch(voiceNoteActions.setLanguage(localeMap[selectLocale] || 'ko'));
      dispatch(translationActions.setLanguage(selectLocale as 'ko' | 'en' | 'ja'));
      let camId = selectedCameraId;
      if (isCamOn && !camId) {
        // 디바이스 초기화(권한 확보 + 열거)가 아직 완료되지 않은 경우 완료를 대기
        try {
          await deviceInitPromiseRef.current;
          const allDevices = await navigator.mediaDevices.enumerateDevices();
          camId = allDevices.find((d) => d.kind === 'videoinput')?.deviceId || '';
        } catch { /* 권한 거부 또는 카메라 없음 시 빈 값 유지 */ }
      }
      if (isCamOn && camId) { dispatch(localActions.setVideoInputDeviceId(camId)); dispatch(localActions.setVideoMuted(false)); }
      else { dispatch(localActions.setVideoInputDeviceId('')); dispatch(localActions.setVideoMuted(true)); }

      if (selectedBg === 'none') dispatch(virtualBackgroundActions.setBackground({ type: 'none' }));
      else if (selectedBg === 'blur') dispatch(virtualBackgroundActions.setBackground({ type: 'blur', blurValue: 25 }));
      else {
        const numId = Number(selectedBg);
        const bg = !isNaN(numId) ? bgImages.find((b) => b.id === numId) : undefined;
        if (bg) dispatch(virtualBackgroundActions.setBackground({ type: 'image', imageUrl: bg.full }));
        else {
          const userBg = userBgImages.find((b) => b.id === selectedBg);
          if (userBg) dispatch(virtualBackgroundActions.setBackground({ type: 'image', imageUrl: userBg.src }));
        }
      }

      if (local.roomId && local.token) await apiManager.setReadyEnterUser(local.roomId, local.token, '2').catch(() => {});

      stopAudioIndicator();
      stopCurrentEffect();
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      if (videoRef.current) videoRef.current.srcObject = null;
      joinedRef.current = true;
      onJoinRoom();
    } catch { setIsJoining(false); }
  };

  const toggleMic = () => setIsMicOn((v) => !v);
  const toggleCam = () => {
    setIsCamOn((v) => {
      if (v) { stopCurrentEffect(); streamRef.current?.getTracks().forEach((t) => t.stop()); streamRef.current = null; if (videoRef.current) videoRef.current.srcObject = null; }
      return !v;
    });
  };
  // ── 렌더링 ──

  // 참석자 영역: 타이틀은 '참석자'로 고정, 카운트는 회의 진행중(접속중)인 인원 기준
  const joinCount = participantList.filter((p) => p.is_joined).length;
  const MAX_SHOW = 5;
  const visibleParticipants = participantList.slice(0, MAX_SHOW);
  const overflowCount = participantList.length - MAX_SHOW;
  const cameraOff = !isCamOn || !selectedCameraId
  return (
    <div className="rtc rtc_new rtc_new3 wrap notosans_font">
      {/* 상단 헤더 */}
      <DefaultHeader />

      {/* 메인 설정 영역 */}
      <div className="rtcNew_container rtcNew_container--setting notosans_font rtcCustoms_preMeeting__container">
        <div className="rtcNew_enterWrap">
          <div className="rtcNew_enterWrap__inBox">
            {/* ── 좌측: 카메라 미리보기 ── */}
            <div className="rtcNew_enterWrap__leftBox">
              <div className={`rtcNew_enterPreview ${!(isAudioDropOpen || isCameraDropOpen) ? 'rtcCustoms_preMeeting__previewOverflow' : ''} ${cameraOff ? getAvatarTag(local.selectedAvatar || 'jangok') : ''}`}>
                {!cameraOff && 
                <div
                  className="screen is_me"
                  style={{
                    backgroundImage: 'none',
                    backgroundColor: isCamOn && selectedCameraId ? 'rgb(245, 245, 247)' : 'rgb(232, 235, 239)',
                    display: 'block',
                    overflow: 'hidden',
                    width: '100%',
                    height: '100%',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 'inherit',
                  }}
                >
                  <span className="blind"></span>
                  {isCamOn && selectedCameraId ? (
                    <video
                      ref={videoRef}
                      className="rtcCustoms_preMeeting__previewVideo"
                      muted
                      autoPlay
                      playsInline
                    />
                  ) : (
                    <div className="rtcCustoms_preMeeting__emptyPreview">
                    </div>
                  )}
                </div>
                }

                {/* 디바이스 버튼 영역 */}
                <div className="rtcNew_enterPreviewBtnBox">
                  {/* 마이크 버튼 + 드롭다운 */}
                  <div className="rtcNew_enterPreviewBtnBox__item">
                    <button
                      type="button"
                      className={`rtcNew_enterPreviewBtn rtcNew_enterPreviewBtn--mic${!isMicOn ? ' disabled' : ''}`}
                      onClick={toggleMic}
                    >
                      <span className="blind"><span>마이크</span></span>
                    </button>
                    <div className="rtcNew_enterPreviewSelect">
                      <button
                        type="button"
                        className="rtcNew_enterPreviewSelectBtn"
                        onClick={() => { setIsAudioDropOpen((v) => !v); setIsCameraDropOpen(false); }}
                      >
                        <span>오디오</span>
                      </button>
                      <div className={`rtcNew_leyarpop${isAudioDropOpen ? ' open' : ''} rtcCustoms_preMeeting__dropZ4`}>
                        <div className="rtcNew_leyarpopInBox">
                          <strong className="rtcNew_leyarpop__title"><span>마이크 선택</span></strong>
                          <ul className="rtcNew_leyarpopList">
                            {mics.map((d) => (
                              <li key={d.deviceId}>
                                <button
                                  type="button"
                                  className={`rtcNew_leyarpopListBtn${selectedMicId === d.deviceId ? ' rtcNew_leyarpopListBtn--selected' : ''}`}
                                  title={d.label || `마이크 ${d.deviceId.slice(0, 8)}`}
                                  onClick={() => { setSelectedMicId(d.deviceId); setIsMicOn(true); setIsAudioDropOpen(false); }}
                                >
                                  {d.label || `마이크 ${d.deviceId.slice(0, 8)}`}
                                </button>
                              </li>
                            ))}
                          </ul>
                          <div className="rtcNew_leyarpopInBox__splitLine"></div>
                          <strong className="rtcNew_leyarpop__title"><span>스피커 선택</span></strong>
                          <ul className="rtcNew_leyarpopList">
                            {speakers.map((d) => (
                              <li key={d.deviceId}>
                                <button
                                  type="button"
                                  className={`rtcNew_leyarpopListBtn${selectedSpeakerId === d.deviceId ? ' rtcNew_leyarpopListBtn--selected' : ''}`}
                                  title={d.label || `스피커 ${d.deviceId.slice(0, 8)}`}
                                  onClick={() => { setSelectedSpeakerId(d.deviceId); setIsAudioDropOpen(false); }}
                                >
                                  {d.label || `스피커 ${d.deviceId.slice(0, 8)}`}
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      <div
                        onClick={() => setIsAudioDropOpen(false)}
                        className="rtcCustoms_preMeeting__dropOverlay"
                        style={{ display: isAudioDropOpen ? 'block' : 'none' }}
                      />
                    </div>
                  </div>

                  {/* 카메라 버튼 + 드롭다운 */}
                  <div className="rtcNew_enterPreviewBtnBox__item">
                    <button
                      type="button"
                      className={`rtcNew_enterPreviewBtn rtcNew_enterPreviewBtn--video${!isCamOn ? ' disabled' : ''}`}
                      onClick={toggleCam}
                    >
                      <span className="blind"><span>카메라</span></span>
                    </button>
                    <div className="rtcNew_enterPreviewSelect">
                      <button
                        type="button"
                        className="rtcNew_enterPreviewSelectBtn"
                        onClick={() => { setIsCameraDropOpen((v) => !v); setIsAudioDropOpen(false); }}
                      >
                        <span>카메라</span>
                      </button>
                      <div className={`rtcNew_leyarpop rtcNew_leyarpop--camera${isCameraDropOpen ? ' open' : ''} rtcCustoms_preMeeting__dropZ4`}>
                        <div className="rtcNew_leyarpopInBox">
                          <strong className="rtcNew_leyarpop__title"><span>카메라 선택</span></strong>
                          <ul className="rtcNew_leyarpopList">
                            {cameras.map((d) => (
                              <li key={d.deviceId}>
                                <button
                                  type="button"
                                  className={`rtcNew_leyarpopListBtn${selectedCameraId === d.deviceId ? ' rtcNew_leyarpopListBtn--selected' : ''}`}
                                  title={d.label || `카메라 ${d.deviceId.slice(0, 8)}`}
                                  onClick={() => { setSelectedCameraId(d.deviceId); setIsCamOn(true); setIsCameraDropOpen(false); }}
                                >
                                  {d.label || `카메라 ${d.deviceId.slice(0, 8)}`}
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                    <div
                      onClick={() => setIsCameraDropOpen(false)}
                      className="rtcCustoms_preMeeting__dropOverlay"
                      style={{ display: isCameraDropOpen ? 'block' : 'none' }}
                    />
                  </div>
                </div>

                {/* 비디오 꺼졌을 때 기본 아바타 선택 리스트 */}
                {(!isCamOn || !selectedCameraId) && (
                  <ul className="defaultProfileList">
                    {AVATAR_LIST.map((avatar) => (
                      <li key={avatar.value} className="rtcCustoms_preMeeting__avatarZ10">
                        <button
                          type="button"
                          className={`defaultProfileList__btn ${local.selectedAvatar === avatar.value ? 'selected' : ''}`}
                          onClick={() => dispatch(localActions.setSelectedAvatar(avatar.value))}
                        >
                          <img src={avatar.img} alt={avatar.label} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* ── 우측: 탭 + 설정 패널 ── */}
            <div className="rtcNew_enterWrap__rightBox">
              {/* 탭 버튼 */}
              <div className="rtcNew_enterWrap__tapBox">
                <button
                  type="button"
                  className={`rtcNew_enterWrap__tapBtn${tab === 'CONFERENCE' ? ' selected' : ''}`}
                  onClick={() => setTab('CONFERENCE')}
                >
                  회의
                </button>
                <button
                  type="button"
                  className={`rtcNew_enterWrap__tapBtn${tab === 'VIRTUAL-BACKGROUND' ? ' selected' : ''}`}
                  onClick={() => setTab('VIRTUAL-BACKGROUND')}
                >
                  가상배경
                </button>
              </div>

              {/* ── 탭: 회의 ── */}
              {tab === 'CONFERENCE' && (
                <>
                  {/* ① 참석자 / 참석 예정자 */}
                  <div className="rtcNew_enterWrap__userlist">
                    <strong className="rtcNew_enterWrap__userlistTitle">
                      <span>참석자</span>
                      &nbsp;<em className="pointColor__blue">{joinCount}</em>&nbsp;<span>명</span>
                    </strong>
                    <ul className="userList">
                      {visibleParticipants.map((p, i) => {
                        const profileUrl = p.user_type === 1 ? toAbsoluteProfileUrl(p.profile_url) : undefined;
                        // 썸네일 클래스 결정
                        let thumClass = 'userList__thum';
                        if (profileUrl) {
                          // 프로필 이미지 있는 사용자
                        } else if (p.user_type === 'email') {
                          thumClass += ' userList__thum--icon iconMail';
                        } else {
                          thumClass += ' userList__thum--icon iconAddr';
                        }
                        if (!p.is_joined) thumClass += ' disabled';

                        return (
                          <li key={p.user || i} className="userList__item">
                            <div
                              className={thumClass}
                              style={profileUrl
                                ? { backgroundImage: `url("${profileUrl}")`, backgroundColor: 'rgba(0,0,0,0)' }
                                : !p.is_joined ? { backgroundImage: 'url("https://static.wehago.com/imgs/common/no_profile.png")', backgroundColor: 'rgba(0,0,0,0)', border: 'none' } : undefined
                              }
                            >
                              {p.is_joined
                                ? <span className="statIcon online">온라인</span>
                                : <span className="statIcon offline">오프라인</span>}
                            </div>
                            <div className="userList__name ellipsis">
                              <span title={p.user_name}>{p.user_name}</span>
                            </div>
                          </li>
                        );
                      })}
                      {overflowCount > 0 && (
                        <li className="userList__item rtcCustoms_preMeeting__cursorPointer" onClick={() => setIsParticipantDialogOpen(true)}>
                          <div className="userList__thum userList__thum--icon iconEtc"></div>
                          <div className="userList__name"><em><span>외 {overflowCount}명</span></em></div>
                        </li>
                      )}
                    </ul>
                  </div>

                  {/* ② 게스트: 참석자명 입력 (v1 리뉴얼: 참여코드 제거됨) */}
                  {local.isGuest && (
                    <div className="rtcNew_enterWrap__section">
                      <dl>
                        <dt>참석자 명</dt>
                        <dd>
                          <div className="common_textInputBox rtcCustoms_preMeeting__guestInput">
                            <input
                              type="text"
                              className="common_textInput"
                              placeholder="화상회의에서 사용할 이름을 설정해 주세요."
                              value={userName}
                              onChange={(e) => {
                                if (e.target.value.length > 50) return;
                                setUserName(e.target.value);
                              }}
                            />
                          </div>
                        </dd>
                      </dl>
                    </div>
                  )}

                  {/* ③ 음성인식 설정 */}
                  <div className="rtcNew_enterWrap__section">
                    <dl>
                      <dt>음성인식 설정</dt>
                      <dd>
                        <div className="common_onoff_switch small2 type_blue2">
                          <label>
                            <input
                              className="onoff_input"
                              type="checkbox"
                              checked={isSpeechEnabled}
                              onChange={(e) => setIsSpeechEnabled(e.target.checked)}
                            />
                            <span className="switch_img"></span>
                            <span className="label_text on"></span>
                            <span className="label_text off"></span>
                          </label>

                          {/* <label>
                            <input
                              className="onoff_input"
                              type="checkbox"
                              checked={isSpeechEnabled}
                              onChange={(e) => setIsSpeechEnabled(e.target.checked)}
                            />
                            <span className="switch_img"></span>
                          </label> */}
                        </div>
                      </dd>
                    </dl>
                    <p className="descText">
                      사용자의 음성을 텍스트로 변환하여<br />
                      자막과 번역을 제공하고, 회의 기록을 확인할 수 있습니다.
                    </p>
                  </div>

                  {/* ④ 자막/번역 언어 */}
                  <div className="rtcNew_enterWrap__section">
                    <dl>
                      <dt>자막/번역 언어</dt>
                      <dd>
                        <div className={"common_component_select v2 rtcCustoms_preMeeting__localeSelect "+ (isLocaleDropOpen ? 'open' : '')}>
                          <button
                            type="button"
                            className="selected_item"
                            onClick={() => setIsLocaleDropOpen((v) => !v)}
                          >
                            {localeOptions.find((o) => o.value === selectLocale)?.label}
                          </button>
                          {isLocaleDropOpen && (
                            <div className="common_component_select_resultbox">
                              <ul>
                                {localeOptions.map((o) => (
                                  <li key={o.value} className={selectLocale === o.value ? 'selected' : ''}>
                                    <button
                                      type="button"
                                      className="resultitem"
                                      onClick={() => { setSelectLocale(o.value); setIsLocaleDropOpen(false); }}
                                    >
                                      {o.label}
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </dd>
                    </dl>
                  </div>
                </>
              )}

              {/* ── 탭: 가상배경 ── */}
              {tab === 'VIRTUAL-BACKGROUND' && (
                <div className="rtcNew_enterWrap__bgBox">
                  <ul className="bgBoxList">
                    {/* + 업로드 버튼 (비회원 숨김) */}
                    {!local.isGuest && (
                      <>
                        <li onClick={() => uploadInputRef.current?.click()}>
                          <div className="bgBoxList__thum bgBoxList__thum--add"></div>
                          <button type="button" className="bgBoxList__btn">
                            <span className="blind">추가하기</span>
                          </button>
                        </li>
                        <input
                          ref={uploadInputRef}
                          className="rtcCustoms_vBg__fileInput"
                          accept=".png,.jpg,.jpeg,.svg"
                          type="file"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) { e.target.value = ''; return; }

                            // 파일 확장자 검증
                            const ext = file.name.split('.').pop()?.toLowerCase() || '';
                            if (!ALLOWED_EXTENSIONS.includes(ext)) {
                              showSnackbar('error', '5MB이하의 이미지 형식만 가능합니다.');
                              e.target.value = ''; return;
                            }

                            // 파일 크기 검증 (5MB)
                            if (file.size > MAX_FILE_SIZE) {
                              showSnackbar('error', '5MB이하의 이미지 형식만 가능합니다.');
                              e.target.value = ''; return;
                            }

                            // 최대 개수 검증
                            if (userBgImages.length >= MAX_USER_BG_MEMBER) {
                              showSnackbar('error', `최대 ${MAX_USER_BG_MEMBER}개까지 추가가 가능합니다.`);
                              e.target.value = ''; return;
                            }

                            const result = await uploadImage(file);
                            if (result) {
                              setSelectedBg(result.id);
                              requestAnimationFrame(() => {
                                const el = document.querySelector(`[data-user-bg-id="${result.id}"]`);
                                el?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
                              });
                            }
                            e.target.value = '';
                          }}
                        />
                      </>
                    )}

                    {/* 없음 */}
                    <li className={selectedBg === 'none' ? 'selected' : ''} onClick={() => setSelectedBg('none')}>
                      <div className="bgBoxList__thum bgBoxList__thum--none">
                        <span className="thumText">없음</span>
                      </div>
                      <button type="button" className="bgBoxList__btn">
                        <span className="blind">선택</span>
                      </button>
                    </li>

                    {/* 흐리게 */}
                    <li className={selectedBg === 'blur' ? 'selected' : ''} onClick={() => setSelectedBg('blur')}>
                      <div className="bgBoxList__thum bgBoxList__thum--blur">
                        <div className="imgMask">
                          <img
                            src="https://static.wehago.com/imgs/rtc/dummy_rtc_bg_00.png"
                            className="bgBoxList__thumImg"
                            alt="배경 이미지"
                          />
                        </div>
                        <span className="thumText">흐리게</span>
                      </div>
                      <button type="button" className="bgBoxList__btn">
                        <span className="blind">선택</span>
                      </button>
                    </li>

                    {/* 기본 이미지 (1~9) */}
                    {bgImages.map((item) => (
                      <li
                        key={`virtual_${item.id}`}
                        className={selectedBg === String(item.id) ? 'selected' : ''}
                        onClick={() => setSelectedBg(String(item.id))}
                      >
                        <div className="bgBoxList__thum">
                          <img src={item.full} className="bgBoxList__thumImg" alt={item.name} />
                        </div>
                        <button type="button" className="bgBoxList__btn">
                          <span className="blind">선택</span>
                        </button>
                      </li>
                    ))}

                    {/* 사용자 업로드 이미지 */}
                    {[...userBgImages].reverse().map((item) => (
                      <li
                        key={`virtual_user_${item.id}`}
                        data-user-bg-id={item.id}
                        className={selectedBg === item.id ? 'selected' : ''}
                        onMouseEnter={() => setHoverBgImageId(item.id)}
                        onMouseLeave={() => setHoverBgImageId(null)}
                        onClick={() => setSelectedBg(item.id)}
                      >
                        <div className="bgBoxList__thum">
                          <img src={item.src} className="bgBoxList__thumImg" alt="사용자 지정 이미지" />
                        </div>
                        <button type="button" className="bgBoxList__btn">
                          <span className="blind">선택</span>
                        </button>
                        {hoverBgImageId === item.id && (
                          <button
                            type="button"
                            className="bgBoxList__delBtn"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteImage(item.id);
                              if (selectedBg === item.id) setSelectedBg('none');
                            }}
                          >
                            <span className="blind">삭제</span>
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* 참여하기 버튼 / 승인 대기 / 예약 대기 */}
          <div className="rtcNew_enterWrap__footer">
            {roomStatus === 'WAITING_APPROVAL' ? (
              <div className="rtcNew_enterWrap__approveBox">
                <div ref={approveLottieRef} className="rtcCustoms_preMeeting__lottieBox" />
                <strong>마스터의 승인을 기다리고 있습니다.</strong>
                <p>마스터가 참석을 승인할 때까지 잠시만 기다려주세요.</p>
              </div>
            ) : roomStatus === 'WAITING' ? (
              <div className="rtcNew_enterWrap__approveBox">
                <div ref={waitingLottieRef} className="rtcCustoms_preMeeting__lottieBox" />
                <strong>{waitingTimeText ? `화상회의 시작 ${waitingTimeText} 전` : '화상회의 잠시 후 시작'}</strong>
                <p>이 회의는 {startDateStr}에 시작 예정입니다.</p>
              </div>
            ) : roomStatus === 'SETUP' ? (
              <button
                type="button"
                className={`common_round_btn rtcNewBtn${isJoining ? ' disabled' : ' blue3'}`}
                id="conference_join_button"
                disabled={isJoining}
                onClick={() => handleJoin()}
              >
                <span>회의 참여하기</span>
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {/* 회의 진입 로딩 오버레이 */}
      {isJoining && (
        <div className="rtcNew_loader">
          <span ref={loaderLottieRef} className="loaderIcon2"></span>
          <p className="rtcNew_loader__text">화상회의가 시작됩니다.</p>
        </div>
      )}

      {/* 참석 예정자 / 참석자 목록 다이얼로그 */}
      <ParticipantListDialog
        open={isParticipantDialogOpen}
        type="conference"
        participantList={participantList}
        onClose={() => setIsParticipantDialogOpen(false)}
      />

      {/* 비밀번호 입력 다이얼로그 오버레이 */}
      {roomStatus === 'PASSWORD_INPUT' && (
        <RoomPassword
          onPasswordCorrect={() => {
            dispatch(uiActions.setRoomStatus('SETUP'));
            handleJoin({ passwordVerified: true });
          }}
          onClose={() => {
            dispatch(uiActions.setRoomStatus('SETUP'));
            dispatch(conferenceActions.setStatus('setup'));
          }}
        />
      )}

    </div>
  );
};

export default RenewalDefaultSetting;
